import React, { Fragment, useState, useMemo } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Input, Badge } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import DataTable from 'react-data-table-component';
import { useGetSuppliersQuery } from '../../../../store/services/inventoryApi';
import { Edit, Eye, Plus, Search } from 'react-feather';
import { useNavigate } from 'react-router-dom';

const SupplierManagement = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');

    // Pass searchTerm to the API hook
    const { data: suppliersData, isLoading, error } = useGetSuppliersQuery({
        search: searchTerm
    });

    const suppliers = useMemo(() => {
        if (!suppliersData) return [];
        return Array.isArray(suppliersData) ? suppliersData : (suppliersData.data || []);
    }, [suppliersData]);

    const columns = [
        {
            name: 'Supplier Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Alias Name',
            selector: row => row.aliasName || '-',
            sortable: true,
        },
        {
            name: 'Contact Person',
            selector: row => row.contactName || row.contactPersonName,
            sortable: true,
        },
        {
            name: 'Email',
            selector: row => row.contactEmail || row.email,
            sortable: true,
        },
        {
            name: 'Phone',
            selector: row => row.contactPhone || row.phone,
            sortable: true,
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            cell: row => (
                <Badge color={row.status?.toUpperCase() === 'ACTIVE' ? 'success' : 'danger'}>
                    {row.status || 'ACTIVE'}
                </Badge>
            )
        },
        {
            name: 'Actions',
            cell: row => {
                const id = row.supplierId || row._id || row.id;
                return (
                    <div>
                        <Button color="info" size="xs" onClick={() => navigate(`/inventory/suppliers/${id}`)} className="me-2" title="View Details">
                            <Eye size={14} />
                        </Button>
                        <Button color="warning" size="xs" onClick={() => navigate(`/inventory/suppliers/edit/${id}`)} title="Edit Supplier">
                            <Edit size={14} />
                        </Button>
                    </div>
                );
            }
        }
    ];

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Supplier Management" parent="Inventory" title="Supplier Management" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h5>Supplier List</h5>
                                <Button color="primary" onClick={() => navigate('/inventory/suppliers/add')}>
                                    <Plus size={16} className="me-2" /> Add Supplier
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <div className="mb-3">
                                    <Row>
                                        <Col md="4">
                                            <div className="input-group">
                                                <span className="input-group-text"><Search size={14} /></span>
                                                <Input
                                                    type="text"
                                                    placeholder="Search by name, email or ID..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                                <div className="table-responsive">
                                    <DataTable
                                        columns={columns}
                                        data={suppliers}
                                        pagination
                                        progressPending={isLoading}
                                        noDataComponent={error ? <div className="p-3">Error loading suppliers. Please check if the server is running.</div> : <div className="p-3">No suppliers found.</div>}
                                        highlightOnHover
                                        striped
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default SupplierManagement;
